import React, { useEffect, useMemo, useRef } from 'react';
import { Select, Table, Pagination, Empty, Flex, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PageScaffold, { PageScaffoldButtonType } from '../../components/PageScaffold';
import TableSkeleton from '../../components/@shared/TableSkeleton';
import { useSubscriptionsPageController, CreditStatusFilter } from './controllers/useSubscriptionsPageController';
import { SubscriptionsPageModals } from './modals/SubscriptionsPageModals';
import { getSubscriptionsColumns } from './subscriptionsPage.columns';
import './SubscriptionsPage.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import CompanyPageHeader from '../company/CompanyPageHeader';
import { getCompanyTabPath } from '../company/useCompanyTabs';

export const SUBSCRIPTIONS_PAGE_PATH = getCompanyTabPath('numbers');
export const SUBRIPTIONS_PAGE_TITLE = 'Numéros';

const STATUS_FILTER_OPTIONS: { value: CreditStatusFilter; label: string }[] = [
  { value: 'ready', label: 'Actif' },
  { value: 'warning', label: 'À surveiller' },
  { value: 'exhausted', label: 'Épuisé' },
  { value: 'overage', label: 'Hors forfait' },
];

interface SubscriptionsPageProps { }

const SubscriptionsPage: React.FC<SubscriptionsPageProps> = () => {
  const { state, actions } = useSubscriptionsPageController();
  const { degradedSubscriptions } = useContext(AuthenticationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const handledLocationKeyRef = useRef<string | null>(null);
  const {
    planFilter,
    statusFilter,
    currentPage,
    pageSize,
    numbers,
    totalNumbers,
    isLoading,
    isReadOnly,
    isCompanyExternallyBilled,
    planMap,
    planFilterOptions,
  } = state;
  const { setSearchValue, setPlanFilter, setStatusFilter, setCurrentPage, openModal, handleUnassignSubscription, handleCancelPlanChange } = actions;

  const columns = getSubscriptionsColumns({ openModal, onUnassign: handleUnassignSubscription, onCancelPlanChange: handleCancelPlanChange, planMap, degradedSubscriptions, isCompanyExternallyBilled });
  const pageTitle = `${SUBRIPTIONS_PAGE_TITLE} (${totalNumbers})`;

  const handleOpenOrderNumber = useMemo(() => {
    return () => {
      if (isCompanyExternallyBilled) {
        message.info('Pour commander un ou plusieurs numéros, merci de contacter directement votre interlocuteur Modelo.');
        return;
      }
      openModal('ORDER_NUMBER');
    };
  }, [isCompanyExternallyBilled, openModal]);

  useEffect(() => {
    const state = location.state as { openModal?: 'ORDER_NUMBER'; statusFilter?: CreditStatusFilter } | null;
    if (!state || handledLocationKeyRef.current === location.key) return;

    if (state.openModal === 'ORDER_NUMBER' || state.statusFilter) {
      handledLocationKeyRef.current = location.key;
      if (state.openModal === 'ORDER_NUMBER') handleOpenOrderNumber();
      if (state.statusFilter) setStatusFilter(state.statusFilter);
      // Clear router state to avoid re-applying on any re-render.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [handleOpenOrderNumber, setStatusFilter, location.key, location.pathname, location.state, navigate]);

  return (
    <PageScaffold
      header={<CompanyPageHeader />}
      title={pageTitle}
      subtitle=" "
      breadcrumbItems={[
        {
          title: ' ',
        },
      ]}
      controlsSettings={{
        searchLabel: 'Numéro, utilisateur',
        searchInputs: (baseInputs) =>
          <Flex align="center" gap={8}>
            {baseInputs}
            <Select
              placeholder="Plan"
              allowClear
              value={planFilter}
              onChange={setPlanFilter}
              className="numbers-page__filter-select"
              style={{ width: 140 }}
              options={planFilterOptions}
            />
            <Select
              placeholder="Statut"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              className="numbers-page__filter-select"
              style={{ width: 140 }}
              options={STATUS_FILTER_OPTIONS}
            />
          </Flex>,
        onSearch: (value) => setSearchValue(value),
        isLoading: false,
        onSearchCancel: () => setSearchValue(''),
        actionButtons: (!isReadOnly ? [{
            label: 'Commander un numéro',
            type: 'primary' as PageScaffoldButtonType,
            props: {
              icon: <PlusOutlined />,
              onClick: handleOpenOrderNumber,
            },
          }] : []),
      }}
    >
      {/* Table */}
      <div className="numbers-page__table">
        {isLoading ? (
          <TableSkeleton columns={columns} rows={pageSize} />
        ) : numbers.length === 0 ? (
          <Empty description="Aucun numéro trouvé" className="numbers-page__empty" />
        ) : (
          <Table
            columns={columns}
            dataSource={numbers}
            rowKey="id"
            pagination={false}
            rowClassName="table-row"
            onRow={(record) => ({
              onClick: () => openModal('CONSUMPTION_HISTORY', record),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </div>

      {/* Pagination */}
      {!isLoading && totalNumbers > pageSize && (
        <div className="numbers-page__pagination">
          <Pagination
            current={currentPage}
            total={totalNumbers}
            pageSize={pageSize}
            onChange={setCurrentPage}
            showSizeChanger={false}
          />
        </div>
      )}

      <SubscriptionsPageModals state={state} actions={actions} />
    </PageScaffold>
  );
};

export default SubscriptionsPage;
